/**
 * Logo Jang-e : la touche « e » d'un clavier, dont le « e » porte le chapeau d'élève.
 * « jàng » = apprendre (wolof), « e » = le numérique. Source unique pour les icônes de l'app,
 * les favicons et les logos des écrans (voir docs/marque.md et apps/mobile/scripts/brand-assets.mts).
 */
export const BRAND = {
  ink: "#15213B",
  teal: "#0E6B66",
  amber: "#E8A317",
  cream: "#F3EEE3",
  white: "#FFFFFF",
} as const;

/** Dessin dans un carré de 100 × 100 ; la touche occupe x 18→82, y 16→84 (centre 50, 50). */
const E_PATH = "M40 53 H60 C60 47.2 55.4 42.6 50 42.6 C44.6 42.6 40 47.2 40 53 C40 58.8 44.6 63.4 50 63.4 C53.8 63.4 56.2 61.7 58 59.6";
const HAT = [
  { d: "M42 36.5 V40.5 Q50 44.5 58 40.5 V36.5 L50 39.5 Z", opacity: 0.8 },
  { d: "M50 27.5 L64 33 L50 38.5 L36 33 Z", opacity: 1 },
  { d: "M50 32.4 Q57 33.6 62 34 L62 35.4 Q57 35 50 33.8 Z", opacity: 1 },
  { d: "M61.2 34.2 H62.8 V40.2 H61.2 Z", opacity: 1 },
  { d: "M61.3 39.6 H62.7 A1.5 1.5 0 0 1 64.2 41.1 V43.1 A1.5 1.5 0 0 1 62.7 44.6 H61.3 A1.5 1.5 0 0 1 59.8 43.1 V41.1 A1.5 1.5 0 0 1 61.3 39.6 Z", opacity: 1 },
];
const KEY_WIDTH = 64;

export type BrandMarkOptions = {
  /** Couleur de la touche. */
  key: string;
  /** Couleur du chapeau (ignorée en monochrome). */
  hat?: string;
  /** Fond plein derrière la touche (icône d'app, favicon) ; absent = transparent. */
  background?: string;
  /** Arrondi du fond, en fraction du côté (0 = carré). */
  radius?: number;
  /** Largeur de la touche en fraction du côté (0.94 = presque bord à bord). */
  scale?: number;
  /** Une seule couleur : le « e » et le chapeau sont découpés dans la touche (icône Android monochrome). */
  mono?: boolean;
  /** Taille en pixels (attributs width/height). */
  size?: number;
  /** Identifiant du masque (unique si plusieurs logos sont insérés dans le même document). */
  id?: string;
};

/** Le logo en SVG autonome. Le « e » est découpé dans la touche : il prend la couleur de ce qui est derrière. */
export function brandMarkSvg(o: BrandMarkOptions): string {
  const id = o.id ?? "jg-mark";
  const k = ((o.scale ?? 0.94) * 100) / KEY_WIDTH;
  const size = o.size ? ` width="${o.size}" height="${o.size}"` : "";
  const bg = o.background ? `<rect width="100" height="100" rx="${(o.radius ?? 0) * 100}" fill="${o.background}"/>` : "";
  const hatCut = o.mono ? HAT.map((h) => `<path d="${h.d}" fill="#000"/>`).join("") : "";
  const hat = o.mono ? "" : HAT.map((h) => `<path d="${h.d}" fill="${o.hat ?? BRAND.amber}"${h.opacity < 1 ? ` fill-opacity="${h.opacity}"` : ""}/>`).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"${size}>`
    + `<defs><mask id="${id}" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100"><rect width="100" height="100" fill="#fff"/>`
    + `<path d="${E_PATH}" fill="none" stroke="#000" stroke-width="5.5" stroke-linecap="round" stroke-linejoin="round"/>${hatCut}</mask></defs>`
    + bg
    + `<g transform="translate(50 50) scale(${k.toFixed(4)}) translate(-50 -50)">`
    + `<g mask="url(#${id})"><rect x="18" y="22" width="64" height="62" rx="15" fill="${o.key}" fill-opacity="0.35"/><rect x="18" y="16" width="64" height="58" rx="15" fill="${o.key}"/></g>`
    + hat
    + `</g></svg>`;
}

/** Le logo en data URI, prêt pour une balise <img>. */
export const brandMarkDataUri = (o: BrandMarkOptions) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(brandMarkSvg(o))}`;
